import React, { useRef, useState } from "react";
import { motion } from "framer-motion";
import { Mail, Phone, MapPin, Loader2, CheckCircle2, ArrowUpRight, Paperclip, X } from "lucide-react";
import { toast } from "sonner";
import { submitContact } from "../../lib/api";
import { TID } from "../../lib/testIds";

const PROJECT_TYPES = [
  "Residential",
  "Commercial",
  "Industrial",
  "Oil & Gas",
  "Airports",
  "Railway Stations",
  "Other",
];

const CONTACT_EMAIL = "alex@akrsteel.com";
const CONTACT_PHONE = "+1 (806) 298-1003";
const CONTACT_PHONE_HREF = "+18062981003";
const CONTACT_ADDRESS_LINE_1 = "3200 Wilcrest Drive, Suite 170";
const CONTACT_ADDRESS_LINE_2 = "Houston, Texas 77042, United States";

export const ContactSection = React.forwardRef((_, ref) => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    project_type: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [pdf, setPdf] = useState(null);
  const fileInputRef = useRef(null);

  const MAX_PDF_BYTES = 10 * 1024 * 1024;

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onPickFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const isPdf = file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf");
    if (!isPdf) {
      toast.error("Only PDF files are accepted.");
      e.target.value = "";
      return;
    }
    if (file.size > MAX_PDF_BYTES) {
      toast.error("PDF must be 10 MB or smaller.");
      e.target.value = "";
      return;
    }
    setPdf(file);
  };

  const clearPdf = () => {
    setPdf(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    try {
      await submitContact({
        name: form.name.trim(),
        email: form.email.trim(),
        company: form.company.trim() || undefined,
        project_type: form.project_type || undefined,
        message: form.message.trim(),
        pdf: pdf || undefined,
      });
      setSuccess(true);
      toast.success("Inquiry received. Our team will be in touch shortly.");
      setForm({ name: "", email: "", company: "", project_type: "", message: "" });
      clearPdf();
    } catch (err) {
      const detail =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Could not submit right now. Please try again.";
      toast.error(typeof detail === "string" ? detail : "Submission failed");
    } finally {
      setLoading(false);
    }
  };

  const formatSize = (b) => (b >= 1024 * 1024 ? `${(b / (1024 * 1024)).toFixed(1)} MB` : `${Math.round(b / 1024)} KB`);

  return (
    <section
      id="contact"
      ref={ref}
      data-testid={TID.contact.root}
      className="relative py-20 md:py-28 bg-[#0A2540] text-white overflow-hidden"
    >
      <div className="absolute inset-0 dot-pattern opacity-[0.06] pointer-events-none" />

      <div className="px-6 md:px-10 lg:px-14 xl:px-16 relative">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-6">
            <div className="inline-flex items-center gap-2 mb-5 text-xs font-mono uppercase tracking-[0.22em] text-slate-400">
              <span className="w-8 h-px bg-slate-500" />
              08 · Let&apos;s build
            </div>
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-semibold tracking-tight leading-[1.1]">
              Have a project?{" "}
              <span className="text-slate-300">Get in touch.</span>
            </h2>
            <p className="mt-5 text-slate-300 text-base md:text-lg leading-relaxed max-w-md">
              Share your project brief — clear-span, tonnage, delivery site — and our
              engineering team will respond within one business day.
            </p>

            <div className="mt-10 space-y-3">
              <a
                href={`mailto:${CONTACT_EMAIL}`}
                data-testid={TID.contact.mailto}
                className="group flex items-center gap-3 text-white/95 hover:text-white transition-colors"
              >
                <span className="shrink-0 w-10 h-10 rounded-full bg-white/10 border border-white/20 flex items-center justify-center group-hover:bg-white/15 transition-colors">
                  <Mail className="w-4 h-4" strokeWidth={2} />
                </span>
                <div className="min-w-0">
                  <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-slate-400">
                    Email
                  </div>
                  <div className="font-display font-semibold truncate">{CONTACT_EMAIL}</div>
                </div>
                <ArrowUpRight className="ml-auto w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" strokeWidth={2.5} />
              </a>

              <a
                href={`tel:${CONTACT_PHONE_HREF}`}
                data-testid="contact-phone-link"
                className="group flex items-center gap-3 text-white/95 hover:text-white transition-colors"
              >
                <span className="shrink-0 w-10 h-10 rounded-full bg-white/10 border border-white/20 flex items-center justify-center group-hover:bg-white/15 transition-colors">
                  <Phone className="w-4 h-4" strokeWidth={2} />
                </span>
                <div className="min-w-0">
                  <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-slate-400">
                    Phone
                  </div>
                  <div className="font-display font-semibold">{CONTACT_PHONE}</div>
                </div>
                <ArrowUpRight className="ml-auto w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" strokeWidth={2.5} />
              </a>

              <div
                data-testid="contact-address"
                className="flex items-start gap-3 text-white/95"
              >
                <span className="shrink-0 w-10 h-10 rounded-full bg-white/10 border border-white/20 flex items-center justify-center">
                  <MapPin className="w-4 h-4" strokeWidth={2} />
                </span>
                <div className="min-w-0">
                  <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-slate-400">
                    Office
                  </div>
                  <div className="font-display font-semibold leading-snug">
                    {CONTACT_ADDRESS_LINE_1}
                    <br />
                    {CONTACT_ADDRESS_LINE_2}
                  </div>
                </div>
              </div>

              <div className="pt-6 border-t border-white/10">
                <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-slate-400 mb-3">
                  Detailing Capacity
                </div>
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { k: "48K", v: "tons / year" },
                    { k: "850+", v: "Projects" },
                    { k: "24hr", v: "Response" },
                  ].map((s) => (
                    <div key={s.v}>
                      <div className="font-display text-2xl font-semibold">{s.k}</div>
                      <div className="text-[10px] font-mono uppercase tracking-widest text-slate-400 mt-0.5">
                        {s.v}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Form */}
          <div className="lg:col-span-6">
            <motion.form
              onSubmit={onSubmit}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.6 }}
              className="bg-white text-slate-900 rounded-2xl p-5 md:p-6 shadow-2xl max-w-xl lg:ml-auto"
            >
              {success ? (
                <div
                  data-testid={TID.contact.success}
                  className="flex flex-col items-center justify-center py-16 text-center"
                >
                  <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center mb-5">
                    <CheckCircle2 className="w-7 h-7 text-green-600" strokeWidth={2} />
                  </div>
                  <h3 className="font-display text-2xl font-semibold text-[#0A2540]">
                    Inquiry received
                  </h3>
                  <p className="mt-2 text-slate-600 max-w-sm">
                    Thanks — our engineering team will reach out within one business day.
                  </p>
                  <button
                    type="button"
                    onClick={() => setSuccess(false)}
                    className="mt-6 text-sm font-medium text-[#0A2540] hover:text-[#0F172A] underline underline-offset-4"
                  >
                    Send another inquiry →
                  </button>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Field label="Full name" required>
                      <input
                        data-testid={TID.contact.name}
                        required
                        value={form.name}
                        onChange={update("name")}
                        placeholder="John Miller"
                        className="w-full px-3 py-2.5 rounded-lg border border-slate-200 focus:border-[#0A2540] focus:outline-none focus:ring-2 focus:ring-[#0A2540]/10 text-sm"
                      />
                    </Field>
                    <Field label="Email" required>
                      <input
                        data-testid={TID.contact.email}
                        required
                        type="email"
                        value={form.email}
                        onChange={update("email")}
                        placeholder="john@company.com"
                        className="w-full px-3 py-2.5 rounded-lg border border-slate-200 focus:border-[#0A2540] focus:outline-none focus:ring-2 focus:ring-[#0A2540]/10 text-sm"
                      />
                    </Field>
                    <Field label="Company">
                      <input
                        data-testid={TID.contact.company}
                        value={form.company}
                        onChange={update("company")}
                        placeholder="Meridian Construction Group"
                        className="w-full px-3 py-2.5 rounded-lg border border-slate-200 focus:border-[#0A2540] focus:outline-none focus:ring-2 focus:ring-[#0A2540]/10 text-sm"
                      />
                    </Field>
                    <Field label="Project type">
                      <select
                        data-testid={TID.contact.projectType}
                        value={form.project_type}
                        onChange={update("project_type")}
                        className="w-full px-3 py-2.5 rounded-lg border border-slate-200 focus:border-[#0A2540] focus:outline-none focus:ring-2 focus:ring-[#0A2540]/10 text-sm bg-white"
                      >
                        <option value="">Select...</option>
                        {PROJECT_TYPES.map((t) => (
                          <option key={t} value={t}>
                            {t}
                          </option>
                        ))}
                      </select>
                    </Field>
                  </div>

                  <div className="mt-4">
                    <Field label="Project brief" required>
                      <textarea
                        data-testid={TID.contact.message}
                        required
                        minLength={5}
                        rows={3}
                        value={form.message}
                        onChange={update("message")}
                        placeholder="Clear-span 200 ft, industrial warehouse, 48,000 sq.ft., site: Houston, TX. Delivery target Q3 2026."
                        className="w-full px-3 py-2.5 rounded-lg border border-slate-200 focus:border-[#0A2540] focus:outline-none focus:ring-2 focus:ring-[#0A2540]/10 text-sm resize-none"
                      />
                    </Field>
                  </div>

                  {/* PDF attachment */}
                  <div className="mt-4">
                    <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-slate-500 mb-1.5">
                      Attach PDF drawing <span className="text-slate-400 normal-case tracking-normal font-sans">(optional · max 10 MB)</span>
                    </div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="application/pdf,.pdf"
                      onChange={onPickFile}
                      data-testid="contact-input-pdf"
                      className="sr-only"
                      id="contact-pdf-input"
                    />
                    {!pdf ? (
                      <label
                        htmlFor="contact-pdf-input"
                        className="flex items-center gap-2.5 w-full px-3 py-2.5 rounded-lg border border-dashed border-slate-300 hover:border-[#0A2540] hover:bg-slate-50 cursor-pointer transition-colors text-sm text-slate-600"
                      >
                        <Paperclip className="w-4 h-4 text-slate-500" strokeWidth={2} />
                        <span>Click to attach a PDF drawing</span>
                      </label>
                    ) : (
                      <div
                        data-testid="contact-pdf-selected"
                        className="flex items-center gap-2.5 w-full px-3 py-2.5 rounded-lg border border-[#0A2540] bg-slate-50 text-sm"
                      >
                        <Paperclip className="w-4 h-4 text-[#0A2540] shrink-0" strokeWidth={2} />
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-[#0A2540] truncate">{pdf.name}</div>
                          <div className="text-[11px] font-mono text-slate-500">{formatSize(pdf.size)}</div>
                        </div>
                        <button
                          type="button"
                          onClick={clearPdf}
                          data-testid="contact-pdf-remove"
                          aria-label="Remove attachment"
                          className="shrink-0 w-7 h-7 rounded-full flex items-center justify-center hover:bg-slate-200 text-slate-500 hover:text-[#0A2540] transition-colors"
                        >
                          <X className="w-4 h-4" strokeWidth={2.25} />
                        </button>
                      </div>
                    )}
                  </div>

                  <div className="mt-6 flex flex-col sm:flex-row sm:items-center gap-4 sm:justify-between">
                    <div className="text-xs text-slate-500">
                      By submitting, you agree to be contacted about your inquiry.
                    </div>
                    <button
                      type="submit"
                      data-testid={TID.contact.submit}
                      disabled={loading}
                      className="inline-flex items-center justify-center gap-2 bg-[#FDBA74] hover:bg-[#FCA55C] disabled:bg-slate-300 disabled:text-slate-500 disabled:cursor-not-allowed text-[#0A2540] text-sm font-semibold px-5 py-2.5 rounded-full transition-colors"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" strokeWidth={2.5} />
                          Sending...
                        </>
                      ) : (
                        <>
                          Send inquiry
                          <ArrowUpRight className="w-4 h-4" strokeWidth={2.5} />
                        </>
                      )}
                    </button>
                  </div>
                </>
              )}
            </motion.form>
          </div>
        </div>
      </div>
    </section>
  );
});

ContactSection.displayName = "ContactSection";

const Field = ({ label, required, children }) => (
  <label className="block">
    <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-slate-500 mb-1.5">
      {label} {required && <span className="text-[#FCA5A5]">*</span>}
    </div>
    {children}
  </label>
);
